/**
 * Lot kartochkasidagi mini-xarita: sun'iy yo'ldosh tasviri ustida uchastka poligoni
 * (yoki geometriya nuqta bo'lsa — joylashuv belgisi). Yengil: MapLibre ishga tushirilmaydi —
 * 256 px'li tayllar va SVG chizma; ma'lumot faqat kartochka ko'rinishga kelganda yuklanadi.
 */
import { useEffect, useMemo, useRef, useState } from "react";

import { auctionApi } from "@/shared/api/auction";
import type { AuctionLotDetail } from "@/shared/api/auction";

const TILE = 256;
const W = 400;
const H = 240;
const TILE_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

type Ring = Array<[number, number]>; // [lon, lat]

const cache = new Map<string, Promise<AuctionLotDetail | null>>();
function loadLot(lotNumber: string) {
  let hit = cache.get(lotNumber);
  if (!hit) {
    hit = auctionApi.lot(lotNumber).catch(() => null);
    cache.set(lotNumber, hit);
  }
  return hit;
}

function collect(lot: AuctionLotDetail): { rings: Ring[]; points: Array<[number, number]> } {
  const rings: Ring[] = [];
  const points: Array<[number, number]> = [];
  const walk = (type: string, coords: unknown) => {
    if (type === "Point") points.push(coords as [number, number]);
    else if (type === "MultiPoint") points.push(...(coords as Array<[number, number]>));
    else if (type === "LineString") rings.push(coords as Ring);
    else if (type === "Polygon") (coords as Ring[]).forEach((ring) => rings.push(ring));
    else if (type === "MultiPolygon") (coords as Ring[][]).forEach((poly) => poly.forEach((ring) => rings.push(ring)));
  };
  for (const feature of lot.geojson?.features ?? []) {
    if (feature.geometry) walk(feature.geometry.type, feature.geometry.coordinates);
  }
  if (!rings.length && !points.length && lot.center) points.push([lot.center.lon, lot.center.lat]);
  return { rings, points };
}

const worldX = (lon: number, z: number) => ((lon + 180) / 360) * TILE * 2 ** z;
const worldY = (lat: number, z: number) => {
  const s = Math.sin((Math.max(-85, Math.min(85, lat)) * Math.PI) / 180);
  return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE * 2 ** z;
};

function MapSvg({ lot }: { lot: AuctionLotDetail }) {
  const view = useMemo(() => {
    const { rings, points } = collect(lot);
    const all = [...rings.flat(), ...points];
    if (!all.length) return null;
    const lons = all.map((c) => c[0]);
    const lats = all.map((c) => c[1]);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    let z = 16;
    if (rings.length) {
      for (; z > 3; z -= 1) {
        const w = worldX(maxLon, z) - worldX(minLon, z);
        const h = worldY(minLat, z) - worldY(maxLat, z);
        if (w <= W * 0.6 && h <= H * 0.6) break;
      }
    }
    const cx = worldX((minLon + maxLon) / 2, z);
    const cy = worldY((minLat + maxLat) / 2, z);
    const ox = cx - W / 2;
    const oy = cy - H / 2;
    const max = 2 ** z;
    const tiles: Array<{ key: string; href: string; x: number; y: number }> = [];
    for (let ty = Math.floor(oy / TILE); ty <= Math.floor((oy + H) / TILE); ty += 1) {
      for (let tx = Math.floor(ox / TILE); tx <= Math.floor((ox + W) / TILE); tx += 1) {
        if (ty < 0 || ty >= max || tx < 0 || tx >= max) continue;
        tiles.push({
          key: `${tx}/${ty}`,
          href: TILE_URL.replace("{z}", String(z)).replace("{x}", String(tx)).replace("{y}", String(ty)),
          x: tx * TILE - ox,
          y: ty * TILE - oy,
        });
      }
    }
    const project = (c: [number, number]) => `${(worldX(c[0], z) - ox).toFixed(1)},${(worldY(c[1], z) - oy).toFixed(1)}`;
    const polys = rings.map((ring) => ring.map(project).join(" "));
    const pins = points.map((c) => {
      const [x, y] = project(c).split(",").map(Number);
      return { x, y };
    });
    // Poligon juda kichik ko'rinsa — markazida belgi ham chiziladi.
    const tiny = rings.length > 0 && worldX(maxLon, z) - worldX(minLon, z) < 14;
    if (tiny) pins.push({ x: W / 2, y: H / 2 });
    return { tiles, polys, pins };
  }, [lot]);

  if (!view) return null;
  return (
    <svg className="lot-map-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" role="img" aria-label="Uchastka joylashuvi">
      <rect width={W} height={H} fill="#1b3a2a" />
      {view.tiles.map((tile) => (
        <image key={tile.key} href={tile.href} x={tile.x} y={tile.y} width={TILE} height={TILE} />
      ))}
      {view.polys.map((points, index) => (
        <polygon key={index} points={points} fill="rgba(34,197,94,0.28)" stroke="#fde047" strokeWidth="2.5" strokeLinejoin="round" />
      ))}
      {view.pins.map((pin, index) => (
        <g key={index} transform={`translate(${pin.x} ${pin.y})`}>
          <path d="M0 0c-9-12-14-18-14-26a14 14 0 1 1 28 0c0 8-5 14-14 26z" fill="#ef4444" stroke="#fff" strokeWidth="2" />
          <circle cy="-26" r="5" fill="#fff" />
        </g>
      ))}
    </svg>
  );
}

export function LotMiniMap({ lotNumber }: { lotNumber: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [lot, setLot] = useState<AuctionLotDetail | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    const start = () => {
      void loadLot(lotNumber).then((data) => {
        if (cancelled) return;
        if (data) setLot(data);
        else setFailed(true);
      });
    };
    if (typeof IntersectionObserver === "undefined") {
      start();
      return () => {
        cancelled = true;
      };
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          start();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [lotNumber]);

  return (
    <div className="lot-map" ref={ref}>
      {lot ? <MapSvg lot={lot} /> : <span className="lot-map-wait">{failed ? "Xarita mavjud emas" : "Xarita yuklanmoqda…"}</span>}
    </div>
  );
}
